import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  NOTIFICATION_SHOW,
  SERVICE_FAILURE,
  USER_ACTIVATE,
  USER_CREATE,
  USER_CREATE_COMPLETED,
  USER_CREATE_VIEW,
  USER_DEACTIVATE,
  USER_EDIT_MODE,
  USER_FIND,
  USER_FIND_ALL,
  USER_FIND_ALL_SUCCESS,
  USER_FIND_SUCCESS,
  USER_REMOVE_COMPLETED,
  USER_RESET_ERROR,
  USER_UPDATE,
  USER_UPDATE_COMPLETED,
  USER_UPDATE_ID,
  USER_UPDATE_LOADING,
  USER_UPDATE_VIEW,
} from '@store/actions';
import { Usuario, UsuarioForm } from '@core/models';
import { UserService } from '@core/services';
import { selectUserForm, selectUserId } from '@store/selectors';

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private userService: UserService
  ) {}

   createForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_CREATE_VIEW),
      mergeMap(() => {
        return [
          USER_EDIT_MODE({ editMode: true, isEditing: false }),
        ];
      }),
    )
  );

  editForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_UPDATE_VIEW),
      mergeMap(({ userId }) => {
        return [
          USER_UPDATE_ID({ userId }),
          USER_EDIT_MODE({ editMode: true, isEditing: true }),
        ];
      }),
    )
  );

  find$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_FIND),
      filter((action) => action.id !== null),
      mergeMap(({ id }) => {
        this.store.dispatch(USER_UPDATE_LOADING({  loading: true }));

        return this.userService.find(id).pipe(
          mergeMap(({ data }) => {
            const [user] = data as Usuario[];

            return [
              USER_UPDATE_LOADING({ loading: false }),
              USER_FIND_SUCCESS({ user }),
            ];
          }),
          catchError((error) => 
            of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  USER_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          )
        )
      }),
    )
  );

  findAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_FIND_ALL),
      mergeMap(({ resellerId, search, stateId }) => {
        this.store.dispatch(USER_UPDATE_LOADING({  loading: true }));

        return this.userService.findAll({ resellerId, search, stateId }).pipe(
          mergeMap(({ data }) => {
            const userList = data as Usuario[];

            return [
              USER_FIND_ALL_SUCCESS({ userList }),
              USER_UPDATE_LOADING({ loading: false }),
            ];
          }),
          catchError((error) => 
            of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  USER_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          )
        )
      }),
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_CREATE),
      withLatestFrom(
        this.store.select(selectUserForm),
      ),
      filter(([_, userForm ]) =>  !!userForm),
      map(([_, userForm]) => ({ userForm: userForm as UsuarioForm })),
      mergeMap(({ userForm }) => {
        this.store.dispatch(USER_UPDATE_LOADING({  loading: true }));

        return this.userService.create(userForm).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'User successfully created' } }),
              USER_UPDATE_LOADING({ loading: false }),
              USER_CREATE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  USER_RESET_ERROR(),
                  USER_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          })
        );
      }),
    )
  );

  creationComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_CREATE_COMPLETED),
      tap(() => {
        this.userService.creationCompleted();
      })
    ),
    { dispatch: false }
  )

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_UPDATE),
      withLatestFrom(
        this.store.select(selectUserId),
        this.store.select(selectUserForm),
      ),
      filter(([_, userId, userForm ]) =>  !!userId && !!userForm),
      map(([_, userId, userForm ]) => ({ id: userId as string, userForm: userForm as UsuarioForm })),
      mergeMap(({ id, userForm }) => {
        this.store.dispatch(USER_UPDATE_LOADING({  loading: true }));

        return this.userService.update(id, userForm).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'User successfully updated' } }),
              USER_UPDATE_LOADING({ loading: false }),
              USER_UPDATE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  USER_RESET_ERROR(),
                  USER_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          })
        );
      }),
    )
  );

  updateComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_UPDATE_COMPLETED),
      tap(() => {
        this.userService.updateCompleted();
      })
    ),
    { dispatch: false }
  )

  activate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_ACTIVATE),
      filter(({ id }) => !!id),
      mergeMap(({ id }) => {

        return this.userService.activate(id).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'User deactivated successfully' } }),
              USER_REMOVE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  USER_RESET_ERROR(),
                ];
              })
            )
          })
        );
      }),
    )
  );

  deactivate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_DEACTIVATE),
      filter(({ id }) => !!id),
      mergeMap(({ id }) => {

        return this.userService.deactivate(id).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'User deactivated successfully' } }),
              USER_REMOVE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  USER_RESET_ERROR(),
                ];
              })
            )
          })
        );
      }),
    )
  );

  deactivateComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_REMOVE_COMPLETED),
      tap(() => {
        this.userService.removeCompleted()
      })
    ),
    { dispatch: false }
  )
}
